import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, FileText, CreditCard, AlertCircle, 
  Loader2, ArrowUpRight, TrendingDown, Landmark 
} from 'lucide-react';
import { reportsService } from '../../services/reportsService';
import { useAuth } from '../../context/AuthContext';
import FaceAttendanceWidget from '../../components/FaceAttendanceWidget';

const FinanceDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Finance manager can see revenue overview and receivables
        const res = await reportsService.getRevenueOverview();
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="animate-spin text-primary w-10 h-10 opacity-20" />
    </div>
  );

  return (
    <div className="space-y-6 text-gray-900">
      {/* Header */}
      <div className="bg-white rounded-card border shadow-soft p-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-black tracking-tight">Finance Overview</h1>
          <p className="text-gray-400 font-bold uppercase text-[10px] tracking-widest mt-1">Logged in as: {user?.role}</p>
        </div>
        <div className="p-3 bg-green-50 text-green-600 rounded-2xl"><Landmark className="w-6 h-6" /></div>
      </div>

      {/* Financial KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <FinanceStat 
          label="Total Contract Value" 
          value={data?.total_contract_value || 0} 
          icon={<FileText className="w-5 h-5" />} 
          color="text-primary" 
          onClick={() => navigate('/contracts')}
        />
        <FinanceStat 
          label="Revenue Received" 
          value={data?.total_received || 0} 
          icon={<CheckCircle2 className="w-5 h-5" />} 
          color="text-green-600" 
          onClick={() => navigate('/reports')}
        />
        <FinanceStat 
          label="Pending Payments" 
          value={data?.total_pending || 0} 
          icon={<AlertCircle className="w-5 h-5" />} 
          color="text-amber-600" 
          onClick={() => navigate('/contracts')}
        />
        <FinanceStat 
          label="Overdue Amount" 
          value={data?.total_overdue || 0} 
          icon={<TrendingDown className="w-5 h-5" />} 
          color="text-red-600" 
          onClick={() => navigate('/reports')}
        />
      </div>

      <FaceAttendanceWidget />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Links */}
        <div className="bg-white rounded-card border border-gray-100 shadow-soft p-6">
           <h2 className="text-xs font-black text-primary uppercase tracking-[0.2em] mb-6 border-b pb-4">Finance Actions</h2>
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ActionButton label="Manage Expenses" desc="Review and approve claims" onClick={() => navigate('/expenses')} />
              <ActionButton label="Process Payroll" desc="Monthly salary batches" onClick={() => navigate('/payroll')} />
              <ActionButton label="Financial Reports" desc="P&L and Cash Flow" onClick={() => navigate('/reports')} />
              <ActionButton label="Contracts" desc="Client payment tracking" onClick={() => navigate('/contracts')} />
           </div>
        </div>

        {/* Info Box - FIXED DESIGN */}
        <div className="bg-primary text-white rounded-card p-8 flex flex-col justify-center relative overflow-hidden shadow-xl shadow-primary/20">
           <div className="relative z-10">
              <h2 className="text-2xl font-black mb-3 tracking-tight">Month-End Reminder</h2>
              <p className="text-white text-sm font-bold leading-relaxed max-w-sm">
                Please ensure all approved commissions are pulled into the upcoming 
                payroll batch before the 30th of this month.
              </p>
           </div>
           <Landmark className="absolute -right-8 -bottom-8 w-48 h-48 text-white opacity-10 rotate-12" />
        </div>
      </div>
    </div>
  );
};

// Sub-components
const FinanceStat = ({ label, value, icon, color, onClick }) => (
  <button onClick={onClick} className="bg-white p-5 rounded-card border border-gray-100 shadow-soft flex items-center gap-4 hover:border-primary/20 transition-all group text-left">
    <div className={'p-3 rounded-xl bg-gray-50 ' + color + ' group-hover:scale-110 transition-transform'}>{icon}</div>
    <div>
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label}</p>
      <p className="text-lg font-black text-gray-900 mt-0.5">{value.toLocaleString()} PKR</p>
    </div>
  </button>
);

const ActionButton = ({ label, desc, onClick }) => (
  <button onClick={onClick} className="p-4 rounded-xl border border-gray-50 bg-gray-50/50 hover:bg-white hover:border-primary/30 hover:shadow-md transition-all text-left group">
    <p className="text-sm font-bold text-gray-900 flex items-center justify-between">
      {label} <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-primary" />
    </p>
    <p className="text-[10px] text-gray-400 font-medium uppercase mt-1">{desc}</p>
  </button>
);

const CheckCircle2 = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);

export default FinanceDashboard;